import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { diceStyle } from '@/features/shop/skins'
import { Face } from './Dice'
import { play } from './sound'

/**
 * Кубики в центре доски.
 *
 * Они лежат там всегда: и выпавшее видно там же, где его бросали, и следить
 * взглядом больше не за чем. Центр для этого единственное свободное место на
 * доске — по правилам он в игре не участвует, кроме своих четырёх углов, куда
 * кубики не достают.
 *
 * Раньше кубики жили в панели, а на бросок улетали в центр и возвращались
 * обратно. Отсюда бралась вся сложность: полёты считались через
 * getBoundingClientRect, панель приходилось прятать на время показа
 * (dice--away), а один и тот же бросок показывался в двух местах — и они
 * умели разъезжаться. Ничего этого больше нет.
 *
 * Что показывается:
 *
 *   - свой бросок ждут — кубики становятся кнопкой и покачиваются;
 *   - бросили — кубики кувыркаются, пока идёт ответ сервера, и ложатся
 *     выпавшим;
 *   - сыгранный кубик гаснет: видно, чем уже сходили;
 *   - чужой бросок показывает очередь показа (usePlayback) — кубики
 *     кувыркаются и ложатся, а очередь ждёт отчёта.
 *
 * Своих задержек компонент не добавляет: темп задаёт сервер, а очередь
 * показа только выстраивает пришедшее по порядку.
 */

/** Грани до первого броска. Не случайные: мигающие числа читаются как результат. */
const IDLE_FACES = [5, 2]

/** Минимальная длительность своего броска: ответ сервера приходит быстрее, чем глаз успевает. */
const ROLL_MS = 600

/**
 * Сколько чужие кубики лежат перед броском.
 *
 * Соперник не бросает в ту же секунду, в какую взял кубики: он смотрит на
 * доску, прикидывает, что ему нужно. Без этой паузы окно броска и сам бросок
 * сливаются в одно движение.
 */
const WATCH_WAIT_MS = 800

/** Сколько крутятся чужие кубики. Меньше своих: ответа сервера ждать не надо. */
const WATCH_SPIN_MS = 240

/** Сколько чужой бросок лежит, прежде чем очередь показа пойдёт дальше. */
const WATCH_MS = 420

/** То же, когда очередь отстала: показ ускоряется, чтобы не отбрасывать сцены. */
const HURRY_WATCH_MS = 200

/**
 * Сколько длится приземление.
 *
 * Кубик не встаёт как вкопанный: он ударяется о доску, чуть проседает и
 * замирает. Без этого бросок кончался обрывом кувырка, и выпавшее выглядело
 * не выпавшим, а подставленным.
 */
const LAND_MS = 320

/**
 * Сколько ждать ответа на свой бросок, прежде чем положить кубики.
 *
 * Страховка, а не такт: ответ приходит за десятки миллисекунд. Но если он не
 * придёт вовсе — упал запрос, пропала сеть, — кубики кувыркались бы до
 * перезагрузки страницы, и это выглядит как зависшая партия.
 */
const SPIN_RESCUE_MS = 5000

/** Сколько кубиков крутить, когда неизвестно точно. */
const DEFAULT_DICE = 2

/** Кубиков за столом всегда два — столько их и лежит в центре. */
const ON_TABLE = 2

/** Границы длительности одного оборота: разная у кубиков и у броска к броску. */
const TUMBLE_MIN_MS = 380
const TUMBLE_MAX_MS = 520

/** Как крутится один кубик: вокруг какой оси и как быстро. */
type Tumble = { axisX: number; axisY: number; ms: number }

/**
 * Раздать кубикам кувырок.
 *
 * Ось вращения — любая в плоскости экрана, а не только вертикальная: кубик
 * с равной вероятностью перекидывается через себя, крутится боком или как-то
 * между. Задаётся углом по кругу, поэтому и «в другую сторону» получается
 * само собой — ось, повёрнутая на 180°, даёт обратное вращение.
 *
 * Скорость тоже своя у каждого. Всё это раздаётся заново на каждый бросок,
 * поэтому одинаковых бросков не бывает: раньше оба кубика всегда
 * переворачивались одинаково, и бросок читался заводным.
 */
function tumbles(): Tumble[] {
  const spin = (): Tumble => {
    const axis = Math.random() * 2 * Math.PI

    return {
      axisX: Number(Math.cos(axis).toFixed(3)),
      axisY: Number(Math.sin(axis).toFixed(3)),
      ms: TUMBLE_MIN_MS + Math.round(Math.random() * (TUMBLE_MAX_MS - TUMBLE_MIN_MS)),
    }
  }

  return Array.from({ length: ON_TABLE }, spin)
}

/**
 * Что лежит на столе после броска.
 *
 * Доброс бывает одиночным: бросают один кубик. Второй при этом никуда не
 * исчезает — он лежит там же, с прежним значением, просто в этом броске не
 * участвует. Раньше он пропадал с доски, и это выглядело поломкой.
 *
 * @param  before  что лежало до броска
 * @param  rolled  что выпало сейчас — ложится первым
 */
function onTable(before: number[], rolled: number[]): number[] {
  return [...rolled, ...before.slice(rolled.length)].slice(0, Math.max(rolled.length, ON_TABLE))
}

/**
 * Стук кубиков — в тот миг, когда они легли.
 *
 * Подаётся ЗДЕСЬ, а не на экране партии, и по той же причине, по какой рубка
 * и дом звучат из очереди показа: только тот, кто ведёт анимацию, знает, когда
 * событие дошло до глаз.
 *
 * До этого стук звонил экран, как только бросок становился текущей сценой. Для
 * своего броска это почти совпадало — кубики крутятся с нажатия. А для чужого
 * расходилось на целую секунду: чужие кубики сперва ЛЕЖАТ 800 мс («соперник
 * думает»), потом 240 мс кувыркаются, и только тогда падают выпавшим. Звук
 * успевал прозвучать до того, как они шевельнулись, — и заметнее всего это
 * было на ходе бота, потому что бот только чужой и бывает.
 *
 * Вне компонента, а не рядом с таймерами: у эффектов ниже узкие списки
 * зависимостей нарочно — новая функция на каждом рендере перезапускала бы их
 * и обрывала заведённый кувырок на середине.
 */
function land(bonus: boolean): void {
  play(bonus ? 'bonus' : 'roll')
}

type Phase = 'still' | 'rolling'

/**
 * Грани куба: противоположные в сумме дают семь, как у настоящего кубика.
 *
 * Куб настоящий, из шести граней, а не квадрат с меняющимися точками. Квадрат
 * в кувырке выглядел картонкой: у него нет толщины, и поворот читался как
 * сплющивание. Заодно отпала подмена граней кодом — в кувырке кубик
 * показывает свои собственные грани, как и положено.
 */
const SIDES = [
  { value: 1, side: 'front' },
  { value: 6, side: 'back' },
  { value: 3, side: 'right' },
  { value: 4, side: 'left' },
  { value: 5, side: 'top' },
  { value: 2, side: 'bottom' },
] as const

/** Поворот куба, при котором нужное значение смотрит на игрока. */
const FACING: Record<number, string> = {
  1: 'rotateY(0deg)',
  2: 'rotateX(90deg)',
  3: 'rotateY(-90deg)',
  4: 'rotateY(90deg)',
  5: 'rotateX(-90deg)',
  6: 'rotateY(180deg)',
}

/** Чужой бросок: что выпало, у кого и не сгорело ли. */
export type WatchedRoll = {
  /** Номер события в ленте: по нему видно, что бросок новый. */
  seq: number
  dice: number[]
  who: string
  burned: boolean
  /** Доброс: у него свой голос, и подаётся он в миг приземления. */
  bonus: boolean
}

type Props = {
  /** Ход ждёт броска именно этого игрока: кубики становятся кнопкой. */
  active: boolean
  /** Сколько кубиков будет брошено: доброс бывает и одиночным. */
  count: number
  /** Последний показанный бросок — он и лежит в центре. */
  shown: { seq: number; dice: number[]; bonus: boolean } | null
  /** Какие из показанных кубиков уже сыграны, по местам в броске. */
  used: boolean[]
  /** Ходить своим выпавшим нечем — кубики сгорели, ход уходит дальше. */
  burned: boolean
  /** Чужой бросок, который очередь показа велела показать сейчас. */
  watch: WatchedRoll | null
  /**
   * Чужой бросок отыгран — очередь может пускать следующую сцену.
   *
   * С номером: пока кубики крутились, очередь могла отбросить сцены, и
   * безымянный отчёт сдвинул бы её мимо нового броска (см. usePlayback).
   */
  onWatched: (seq: number) => void
  /** Очередь показа отстала: чужой бросок показывается коротко. */
  hurry: boolean
  onRoll: () => void
  /**
   * Кубики того, чей бросок показан (features/shop): их видит весь стол.
   * null — обычные, цветов темы.
   */
  skin?: string | null
}

export function CentreDice({
  active,
  count,
  shown,
  used,
  burned,
  watch,
  onWatched,
  hurry,
  onRoll,
  skin = null,
}: Props) {
  const [phase, setPhase] = useState<Phase>('still')
  /**
   * Стол: что лежит в центре и сколько из этого — текущий бросок.
   *
   * Одним состоянием, а не двумя: значения и их число меняются только вместе,
   * и раздельными состояниями между ними успевал бы вклиниться рендер, в
   * котором кубиков больше, чем известно про них.
   */
  const [table, setTable] = useState<{ dice: number[]; live: number }>(() => {
    const dice = shown?.dice ?? IDLE_FACES

    return { dice, live: dice.length }
  })
  /** Кубики только что легли — играется удар о доску. */
  const [landing, setLanding] = useState(false)
  /** Куда и как быстро крутится каждый кубик в этом броске. */
  const [spins, setSpins] = useState<Tumble[]>(tumbles)
  const rolledAt = useRef(0)
  /**
   * Что лежало в центре, когда мы взяли кубики.
   *
   * По нему видно, что ответ на СВОЙ бросок пришёл: в ленте появился новый
   * бросок, а не тот же, что лежал до нажатия.
   */
  const rolledFrom = useRef<number | null>(null)
  /** Кубики только что кувыркались: по этому и играется приземление. */
  const tumbled = useRef(false)
  /**
   * Номер чужого броска, который уже долетел и лежит на столе.
   *
   * Ref, а не проверка «phase === 'still'»: в 'rolling' фаза переводится
   * асинхронно, эффектом ниже, уже ПОСЛЕ того как watchSeq сменился. На
   * первый рендер нового броска phase ещё старая — если ею кончалась
   * предыдущая, тоже сгоревшая, сцена, это 'still', и «ходить нечем»
   * успевало показаться, погаснуть на кувырке и появиться снова, называя
   * выпавшее раньше, чем кубики долетели до глаз.
   */
  const landedWatch = useRef<number | null>(null)

  /*
   * Показ чужого броска заводится по НОМЕРУ, а не по объекту: объект приходит
   * новый на каждом рендере, и эффект перезапускался бы, обрывая заведённые
   * таймеры на середине. Всё, что может измениться по ходу показа, читается
   * из ссылок.
   */
  const watchSeq = watch?.seq ?? null
  const latest = useRef({ watch, hurry, onWatched })
  latest.current = { watch, hurry, onWatched }

  // Чужой бросок: полежал, кувыркнулся, лёг выпавшим — и очередь идёт дальше.
  useEffect(() => {
    if (watchSeq === null) return

    const { watch: roll, hurry: rushed, onWatched: report } = latest.current
    const dice = roll?.dice ?? []
    const wait = rushed ? 0 : WATCH_WAIT_MS
    const hold = rushed ? HURRY_WATCH_MS : WATCH_MS

    const timers = [
      setTimeout(() => {
        setSpins(tumbles())
        setPhase('rolling')
      }, wait),
      setTimeout(() => {
        setTable((before) => ({ dice: onTable(before.dice, dice), live: dice.length }))
        setPhase('still')
        landedWatch.current = watchSeq
        // Стук — ровно здесь, когда кубики легли: см. комментарий у land().
        land(roll?.bonus === true)
      }, wait + WATCH_SPIN_MS),
      setTimeout(() => report(watchSeq), wait + WATCH_SPIN_MS + hold),
    ]

    return () => timers.forEach(clearTimeout)
  }, [watchSeq])

  /*
   * Ответ на свой бросок пришёл — в ленте лежит уже новый бросок.
   *
   * Одного «ход больше не ждёт броска» мало: бросок, которым ходить нечем,
   * сгорает, а заработанный им доброс остаётся — и ход снова ждёт броска
   * (docs/RULES.md, решение 1). Приглашение при этом не пропадает, и по нему
   * одному ответ от его ожидания не отличить: кубики кувыркались до
   * перезагрузки страницы. Чаще всего это конец партии, где последней фишке
   * нужно точное число, а выпадает шестёрка.
   */
  const answered = shown !== null && shown.seq !== rolledFrom.current

  /*
   * Свой бросок: ответ пришёл — кубики докручиваются до ROLL_MS, чтобы бросок
   * было видно. Выпавшее подставит эффект ниже: оно приходит из ленты вместе
   * с ответом.
   */
  useEffect(() => {
    if (phase !== 'rolling' || watchSeq !== null || (active && !answered)) return

    const wait = Math.max(0, ROLL_MS - (Date.now() - rolledAt.current))
    const done = setTimeout(() => {
      setPhase('still')
      land(shown?.bonus === true)
    }, wait)

    return () => clearTimeout(done)
  }, [phase, watchSeq, active, answered, shown?.bonus])

  // ...но не дольше страховочного срока: молчание сервера не должно оставлять
  // кубики кувыркаться навсегда.
  useEffect(() => {
    if (phase !== 'rolling' || watchSeq !== null) return

    const rescue = setTimeout(() => setPhase('still'), SPIN_RESCUE_MS)

    return () => clearTimeout(rescue)
  }, [phase, watchSeq])

  // Ничего не происходит — в центре лежит последний показанный бросок.
  useEffect(() => {
    if (phase !== 'still' || watchSeq !== null || shown === null) return

    setTable((before) => ({ dice: onTable(before.dice, shown.dice), live: shown.dice.length }))
  }, [phase, watchSeq, shown])

  /*
   * Кувырок кончился — кубики ударяются о доску и замирают.
   *
   * По прошлой фазе, а не в уборке эффекта кувырка: уборка срабатывает ещё и
   * при размонтировании, то есть трогала бы состояние уходящего компонента.
   */
  useEffect(() => {
    if (phase === 'rolling') {
      tumbled.current = true

      return
    }

    if (!tumbled.current) return

    tumbled.current = false
    setLanding(true)

    const done = setTimeout(() => setLanding(false), LAND_MS)

    return () => clearTimeout(done)
  }, [phase])

  /*
   * Кувыркаются только те кубики, которые брошены: при одиночном добросе
   * второй лежит на месте. Значения кувыркающихся неизвестны (свой бросок
   * ждёт ответа сервера) — за поворотом их всё равно не видно, поэтому
   * остаются прежние.
   */
  const rolling = phase === 'rolling'
  const live = rolling
    ? Math.max(1, watchSeq === null ? count : (latest.current.watch?.dice.length ?? DEFAULT_DICE))
    : table.live
  const dice =
    table.dice.length < live ? onTable(table.dice, table.dice.slice(0, live)) : table.dice
  const asking = active && phase !== 'rolling'
  const watching = watchSeq !== null
  /*
   * Своё «ходить нечем» показывается и на приглашении бросить: сгоревший
   * доброс оставляет ход за нами, и объяснять его надо ровно тогда, когда
   * игрок снова тянется к кубикам.
   *
   * Чужое — только когда СЕЙЧАС ПОКАЗАННЫЙ бросок и есть тот, что долетел
   * (watchSeq === landedWatch.current): «phase === 'still'» одного этого не
   * гарантирует, см. комментарий у landedWatch выше.
   */
  const burnedNow =
    phase === 'still' &&
    (watching ? watchSeq === landedWatch.current && latest.current.watch?.burned === true : burned)

  return (
    <div className={`centre-dice${asking ? ' centre-dice--asking' : ''}`} style={diceStyle(skin)}>
      <button
        className="centre-dice__button"
        type="button"
        aria-label="Бросить кубики"
        disabled={!asking}
        onClick={() => {
          rolledAt.current = Date.now()
          rolledFrom.current = shown?.seq ?? null
          setSpins(tumbles())
          setPhase('rolling')
          onRoll()
        }}
      >
        <span className="centre-dice__row">
          {dice.map((value, index) => (
            <span
              // Кубики различаются только местом в броске: одинаковые
              // значения не просто допустимы, а обычны.
              // biome-ignore lint/suspicious/noArrayIndexKey: см. выше
              key={`centre-die-${index}`}
              /*
               * Гаснет и сыгранный кубик, и тот, что в этом броске не
               * участвует: и тем и другим сейчас не ходят, а разделять
               * два оттенка серого игрок всё равно не станет.
               */
              className={`die die--cube${rolling && index < live ? ' die--spinning' : ''}${
                landing && !rolling ? ' die--landing' : ''
              }${index >= live || (!rolling && used[index] === true) ? ' die--used' : ''}`}
              /*
               * Кувырок задаётся переменными, а не классами: направлений и
               * скоростей больше, чем разумно описать правилами, а
               * наследование донесёт их до самого куба.
               */
              style={
                {
                  '--axis-x': spins[index]?.axisX ?? 1,
                  '--axis-y': spins[index]?.axisY ?? 0,
                  '--spin-ms': `${spins[index]?.ms ?? TUMBLE_MIN_MS}ms`,
                } as CSSProperties
              }
            >
              {/*
                Поворот куба — инлайном: он зависит от выпавшего значения, а
                таких правил в стилях было бы шесть на каждый кубик. Анимация
                кувырка его перебивает сама: она весит больше обычных
                объявлений, включая инлайновые.
              */}
              {/*
                В покое куб смотрит гранью строго на игрока: любой наклон при
                близкой перспективе превращает квадрат в трапецию, и кубик
                читается не кубиком, а скошенной картонкой. Объём показывает
                кувырок, а лежат кубики просто под небольшим углом друг
                к другу — это делают стили.
              */}
              <span className="die__box" style={{ transform: FACING[value] ?? FACING[1] }}>
                {SIDES.map((face) => (
                  <span key={face.side} className={`die__face die__face--${face.side}`}>
                    <Face value={face.value} />
                  </span>
                ))}
              </span>
            </span>
          ))}
        </span>

        {/*
          Призыв бросить — крупно ПОВЕРХ кубиков.
          Подписью под ними он не работал: мелкая серая строка в 0.95rem
          читалась как пояснение к картинке, а не как «сейчас твой ход», и
          её приходилось искать глазами. Вдобавок на телефоне она вылезала
          из центрального квадрата на клетки и мешала по ним нажимать.

          Перекрывать кубики можно смело: до броска они ничего не сообщают —
          лежат IDLE_FACES, декоративные числа. А как только бросок начался,
          призыв пропадает и кубики видно целиком.
        */}
        <span className={`centre-dice__call${asking ? '' : ' centre-dice__call--hidden'}`}>
          ROLL
        </span>
        {/*
          «Ходить нечем» — над кубиками, а не под: под ними теперь ничего
          нет, а внизу центрального квадрата строка упиралась в клетки.
          Место держится всегда, иначе строка появлялась бы рывком.
        */}
        <span className={`centre-dice__verdict${burnedNow ? '' : ' centre-dice__verdict--hidden'}`}>
          ходить нечем
        </span>
      </button>
    </div>
  )
}
